import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight, BarChart3, Users, Mail, Shield, Sparkles, Target,
    Brain, Zap, Globe, Layers, CheckCircle2, Star, ChevronRight,
    MessageSquare, LineChart, Building2, Workflow, Eye, PenTool,
    MousePointerClick, FileText, Palette, Bot, UserCheck, TrendingUp,
    Clock, LayoutGrid, Lock, ChevronDown
} from 'lucide-react';

const NAV_LINKS = [
    { label: 'Features', href: '#features' },
    { label: 'AI Engine', href: '#ai-engine' },
    { label: 'Website Builder', href: '#website-builder' },
    { label: 'Pricing', href: '#pricing' },
];

const HERO_STATS = [
    { value: '10x', label: 'Faster email personalization' },
    { value: '360\u00B0', label: 'Relationship intelligence' },
    { value: '24/7', label: 'AI-powered insights' },
];

const PLATFORM_FEATURES = [
    {
        icon: Users,
        title: 'Contact & Company Management',
        desc: 'Track every relationship with DISC personality profiling, org charts, and buying committees. Know your prospects before you reach out.',
        highlights: ['DISC personality analysis', 'Org chart mapping', 'Custom fields & tags'],
    },
    {
        icon: BarChart3,
        title: 'Visual Pipeline & Forecasting',
        desc: 'Kanban boards, list views, and custom deal stages. AI-powered deal risk analysis tells you which deals need attention right now.',
        highlights: ['Kanban & list views', 'AI deal risk scoring', 'Revenue forecasting'],
    },
    {
        icon: Mail,
        title: 'Email Sequences & Campaigns',
        desc: 'Multi-step automated outreach with AI rewrite suggestions. Campaign journey builder with visual flow editor for complex nurture paths.',
        highlights: ['Visual campaign builder', 'Smart scheduling', 'A/B testing'],
    },
    {
        icon: Target,
        title: 'Deal Rooms & Proposals',
        desc: 'Share branded proposals, track document engagement, and collaborate with buyers in dedicated deal rooms.',
        highlights: ['Client-facing portals', 'Document tracking', 'E-signatures'],
    },
    {
        icon: MessageSquare,
        title: 'Gmail & Calendar Sync',
        desc: 'Two-way Gmail sync with AI-powered email coaching. Google Calendar integration for booking links and meeting scheduling.',
        highlights: ['Two-way email sync', 'Booking pages', 'Meeting prep briefs'],
    },
    {
        icon: Layers,
        title: 'Import, Enrich & Customize',
        desc: 'CSV import wizard with smart field mapping. AI data enrichment fills in the gaps. Custom fields make it yours.',
        highlights: ['Smart CSV import', 'AI enrichment', 'Custom fields & views'],
    },
];

const AGENTIC_STEPS = [
    {
        step: '01',
        icon: FileText,
        title: 'Deep Context Files',
        desc: 'AI builds rich markdown profiles for every contact, company, and communication pair. Continuously updated with the latest CRM data, deal context, and interaction history.',
        color: 'violet',
    },
    {
        step: '02',
        icon: PenTool,
        title: 'AI Writes Unique Emails',
        desc: 'Each email is crafted from scratch using your voice profile, the contact\'s DISC personality, account intelligence, and communication history. No templates, no mail merge \u2014 true 1:1 personalization.',
        color: 'violet',
    },
    {
        step: '03',
        icon: Eye,
        title: 'Human-in-the-Loop Review',
        desc: 'Every AI draft lands in your review queue with a 5-dimension quality score. Approve, edit, or regenerate with guidance. You stay in control while AI does the heavy lifting.',
        color: 'violet',
    },
    {
        step: '04',
        icon: Brain,
        title: 'AI Gets Smarter Over Time',
        desc: 'Your edits and feedback train the system. Rejection reasons and edit patterns are analyzed to improve future drafts. The more you use it, the better it gets.',
        color: 'violet',
    },
];

const BUILDER_FEATURES = [
    {
        icon: LayoutGrid,
        title: 'Drag & Drop Builder',
        desc: 'Build beautiful pages with an intuitive visual editor. No code required \u2014 just drag, drop, and publish.',
    },
    {
        icon: Workflow,
        title: 'Multi-Step Funnels',
        desc: 'Create conversion funnels with landing pages, forms, thank-you pages, and automated follow-up sequences.',
    },
    {
        icon: Palette,
        title: 'Custom Branding',
        desc: 'Match your brand with custom colors, fonts, logos, and domain mapping. Your pages, your identity.',
    },
    {
        icon: MousePointerClick,
        title: 'Built-in Analytics',
        desc: 'Track page views, form submissions, and conversion rates. See which funnels drive the most pipeline.',
    },
    {
        icon: Zap,
        title: 'CRM-Connected Forms',
        desc: 'Form submissions automatically create contacts and deals in your CRM. No Zapier needed \u2014 it\'s native.',
    },
    {
        icon: Globe,
        title: 'Custom Domains & SSL',
        desc: 'Publish to your own domain with automatic SSL. Professional pages that build trust with prospects.',
    },
];

const PRICING_TIERS = [
    {
        name: 'Starter',
        price: 'Free',
        period: 'forever',
        desc: 'For individuals getting started with CRM.',
        features: [
            'Up to 500 contacts',
            'Basic pipeline management',
            'Email sequences (3 active)',
            'Gmail & Calendar sync',
            'CSV import',
        ],
        cta: 'Start Free',
        highlighted: false,
    },
    {
        name: 'Pro',
        price: '$49',
        period: '/user/month',
        desc: 'For growing teams that want AI superpowers.',
        features: [
            'Unlimited contacts',
            'Agentic Personalization',
            'AI deal risk analysis',
            'Website & funnel builder',
            'Campaign journey builder',
            'DISC personality profiling',
            'Custom fields & views',
            'Deal rooms & proposals',
            'Priority support',
        ],
        cta: 'Start 14-Day Trial',
        highlighted: true,
        badge: 'Most Popular',
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        period: '',
        desc: 'For teams that need scale, security & control.',
        features: [
            'Everything in Pro',
            'Unlimited websites & funnels',
            'Advanced AI customization',
            'Custom integrations',
            'Dedicated account manager',
            'SSO & advanced security',
            'SLA & uptime guarantee',
        ],
        cta: 'Contact Sales',
        highlighted: false,
    },
];

const TESTIMONIAL_LOGOS = [
    'Sales teams', 'Agencies', 'Consultants', 'SaaS companies', 'Professional services'
];

function SmoothScrollLink({ href, children, className }) {
    const handleClick = (e) => {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) target.scrollIntoView({ behavior: 'smooth' });
    };
    return <a href={href} onClick={handleClick} className={className}>{children}</a>;
}

export default function LandingPage() {
    const [openFaq, setOpenFaq] = useState(null);

    const faqs = [
        {
            q: 'How is Agentic Personalization different from mail merge?',
            a: 'Mail merge swaps in variables like {first_name}. Agentic Personalization writes entirely unique emails from scratch using AI that understands your voice, the recipient\'s personality, your shared history, and current deal context. Every email is different.',
        },
        {
            q: 'Do I need technical skills to use the website builder?',
            a: 'Not at all. The drag-and-drop builder is designed for sales and marketing teams. Pick a template, customize it with your brand, connect your forms to the CRM, and publish \u2014 all without writing a single line of code.',
        },
        {
            q: 'Can I import my existing contacts and deals?',
            a: 'Yes. Our smart CSV import wizard automatically maps your columns to CRM fields. You can also use AI enrichment to fill in missing data like company info, social profiles, and DISC personality estimates.',
        },
        {
            q: 'Is my data secure?',
            a: 'Absolutely. We use multi-tenant architecture with strict data isolation, role-based access controls, and encrypted connections. Your CRM data is never used to train AI models.',
        },
    ];

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
            {/* Nav */}
            <nav className="border-b border-stone-200/60 sticky top-0 z-50 backdrop-blur-md" style={{ backgroundColor: 'rgba(250, 250, 245, 0.9)' }}>
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-stone-900 flex items-center justify-center">
                            <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-lg font-semibold text-stone-900 tracking-tight" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>NurtureCRM</span>
                    </div>
                    <div className="hidden md:flex items-center gap-6">
                        {NAV_LINKS.map(link => (
                            <SmoothScrollLink key={link.href} href={link.href} className="text-sm text-stone-500 hover:text-stone-900 transition-colors font-medium">
                                {link.label}
                            </SmoothScrollLink>
                        ))}
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/login" className="text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors px-4 py-2">
                            Login
                        </Link>
                        <Link to="/register" className="text-sm font-medium bg-stone-900 text-white px-5 py-2 rounded-lg hover:bg-stone-800 transition-colors">
                            Get Started
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-20 pb-16 md:pt-32 md:pb-24 relative overflow-hidden">
                {/* Subtle gradient bg */}
                <div className="absolute inset-0 bg-gradient-to-b from-violet-50/30 via-transparent to-transparent pointer-events-none" />
                <div className="max-w-5xl mx-auto px-6 text-center relative">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-100/80 text-violet-800 text-xs font-semibold mb-8 border border-violet-200/50">
                        <Sparkles className="w-3.5 h-3.5" />
                        NEW: Agentic Personalization — AI writes unique emails for every contact
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 mb-6 leading-[1.08]" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        The AI-Powered CRM That<br className="hidden sm:block" />
                        <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent"> Sells For You</span>
                    </h1>
                    <p className="text-lg md:text-xl text-stone-500 mb-10 max-w-3xl mx-auto leading-relaxed">
                        Full pipeline CRM with AI that writes personalized emails, builds your website and funnels, coaches your communication style, and analyzes every deal. All in one platform.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                        <Link to="/register" className="inline-flex items-center gap-2 text-base font-semibold bg-stone-900 text-white px-8 py-3.5 rounded-xl hover:bg-stone-800 transition-all shadow-lg shadow-stone-900/10 hover:shadow-xl hover:shadow-stone-900/15">
                            Start Free — No Card Required <ArrowRight className="w-4 h-4" />
                        </Link>
                        <SmoothScrollLink href="#ai-engine" className="inline-flex items-center gap-2 text-base font-medium border border-stone-300 text-stone-700 px-8 py-3.5 rounded-xl hover:bg-white transition-colors">
                            See AI in Action
                        </SmoothScrollLink>
                    </div>
                    {/* Stats row */}
                    <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
                        {HERO_STATS.map((stat, i) => (
                            <div key={i} className="text-center">
                                <div className="text-2xl md:text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>{stat.value}</div>
                                <div className="text-xs md:text-sm text-stone-400 mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trusted by */}
            <section className="py-8 border-y border-stone-200/40">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
                        <span className="text-xs text-stone-400 uppercase tracking-wider font-medium">Built for</span>
                        {TESTIMONIAL_LOGOS.map((name, i) => (
                            <span key={i} className="text-sm font-medium text-stone-400">{name}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Platform Features */}
            <section id="features" className="py-20 md:py-28">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-xs font-semibold mb-4 border border-stone-200/60">
                            PLATFORM
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            Everything Your Sales Team Needs
                        </h2>
                        <p className="text-stone-500 text-lg max-w-2xl mx-auto">A complete B2B sales platform — from first touch to closed deal.</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {PLATFORM_FEATURES.map((f, i) => (
                            <div key={i} className="bg-white rounded-2xl border border-stone-200/60 p-7 hover:shadow-lg hover:border-stone-300/60 transition-all group">
                                <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center mb-5 group-hover:bg-stone-900 transition-colors">
                                    <f.icon className="w-5.5 h-5.5 text-stone-600 group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="text-lg font-semibold text-stone-900 mb-2">{f.title}</h3>
                                <p className="text-sm text-stone-500 leading-relaxed mb-4">{f.desc}</p>
                                <div className="space-y-1.5">
                                    {f.highlights.map((h, j) => (
                                        <div key={j} className="flex items-center gap-2 text-xs text-stone-500">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                            {h}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Agentic Personalization — Star Feature */}
            <section id="ai-engine" className="py-20 md:py-28 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-50/50 via-purple-50/30 to-transparent pointer-events-none" />
                <div className="max-w-6xl mx-auto px-6 relative">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold mb-4 border border-violet-200/60">
                            <Sparkles className="w-3.5 h-3.5" />
                            STAR FEATURE
                        </div>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-5" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            Agentic Personalization
                        </h2>
                        <p className="text-stone-500 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
                            AI that writes truly unique emails for every contact — using your voice, their personality, your shared history, and the deal context. Not templates. Not mail merge. Real personalization at scale.
                        </p>
                    </div>

                    {/* How it works steps */}
                    <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                        {AGENTIC_STEPS.map((step, i) => (
                            <div key={i} className="bg-white rounded-2xl border border-violet-200/40 p-7 hover:shadow-lg transition-all relative overflow-hidden group">
                                <div className="absolute top-4 right-5 text-5xl font-black text-violet-100/60 select-none" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                                    {step.step}
                                </div>
                                <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center mb-4 group-hover:bg-violet-600 transition-colors">
                                    <step.icon className="w-5 h-5 text-violet-600 group-hover:text-white transition-colors" />
                                </div>
                                <h3 className="text-lg font-semibold text-stone-900 mb-2 relative">{step.title}</h3>
                                <p className="text-sm text-stone-500 leading-relaxed relative">{step.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Key differentiators */}
                    <div className="mt-14 max-w-4xl mx-auto">
                        <div className="grid sm:grid-cols-3 gap-6">
                            {[
                                { icon: Bot, title: 'Your Voice, Amplified', desc: 'AI learns your writing style and tone from a configurable voice profile. Emails sound like you, not a robot.' },
                                { icon: UserCheck, title: 'DISC-Aware Messaging', desc: 'Adapts tone and structure to each contact\'s DISC personality. Direct for D types, warm for I types, detailed for C types.' },
                                { icon: TrendingUp, title: '5-Score Quality Check', desc: 'Every draft is scored on voice match, DISC alignment, relevance, clarity, and CTA strength before it reaches your queue.' },
                            ].map((d, i) => (
                                <div key={i} className="text-center p-5">
                                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                                        <d.icon className="w-5 h-5 text-violet-600" />
                                    </div>
                                    <h4 className="text-sm font-semibold text-stone-900 mb-1">{d.title}</h4>
                                    <p className="text-xs text-stone-500 leading-relaxed">{d.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Website & Funnel Builder */}
            <section id="website-builder" className="py-20 md:py-28" style={{ backgroundColor: 'rgba(0,0,0,0.015)' }}>
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-4 border border-emerald-200/60">
                                <Globe className="w-3.5 h-3.5" />
                                WEBSITE & FUNNEL BUILDER
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-5" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                                Build Landing Pages & Funnels That Convert
                            </h2>
                            <p className="text-stone-500 text-lg leading-relaxed mb-8">
                                Create stunning landing pages, capture forms, and multi-step conversion funnels — all connected directly to your CRM. Every form submission becomes a contact, every page view becomes an insight.
                            </p>
                            <div className="space-y-4 mb-8">
                                {[
                                    'Drag-and-drop page builder with pre-built templates',
                                    'Multi-step funnels with conversion tracking',
                                    'Forms auto-create CRM contacts & deals',
                                    'Custom domains with automatic SSL',
                                    'Built-in analytics — no extra tools needed',
                                ].map((item, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                        <span className="text-sm text-stone-600">{item}</span>
                                    </div>
                                ))}
                            </div>
                            <Link to="/register" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors">
                                Try the builder free <ChevronRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {BUILDER_FEATURES.map((f, i) => (
                                <div key={i} className="bg-white rounded-xl border border-stone-200/60 p-5 hover:shadow-md transition-all">
                                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center mb-3">
                                        <f.icon className="w-4.5 h-4.5 text-emerald-600" />
                                    </div>
                                    <h4 className="text-sm font-semibold text-stone-900 mb-1">{f.title}</h4>
                                    <p className="text-xs text-stone-500 leading-relaxed">{f.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* AI Features Highlight Bar */}
            <section className="py-16 bg-stone-900 text-white">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            AI Built Into Every Corner
                        </h2>
                        <p className="text-stone-400 text-base max-w-2xl mx-auto">Not a bolt-on. AI is woven into the fabric of every feature.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Brain, title: 'Sales Assistant', desc: 'Chat with an AI that knows your pipeline, contacts, and deals inside and out.' },
                            { icon: LineChart, title: 'Deal Risk Analysis', desc: 'AI scores deal health and flags risks before they become losses.' },
                            { icon: MessageSquare, title: 'Communication Coach', desc: 'DISC-based coaching on every email. Learn to adapt your style.' },
                            { icon: Clock, title: 'Daily Briefings', desc: 'AI-generated morning brief with priorities, risks, and opportunities.' },
                        ].map((f, i) => (
                            <div key={i} className="bg-white/5 rounded-xl border border-white/10 p-6 hover:bg-white/10 transition-colors">
                                <f.icon className="w-6 h-6 text-violet-400 mb-3" />
                                <h4 className="text-sm font-semibold text-white mb-1.5">{f.title}</h4>
                                <p className="text-xs text-stone-400 leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="py-20 md:py-28">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-14">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-xs font-semibold mb-4 border border-stone-200/60">
                            PRICING
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            Simple, Transparent Pricing
                        </h2>
                        <p className="text-stone-500 text-lg max-w-2xl mx-auto">Start free, upgrade when you need AI superpowers.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {PRICING_TIERS.map((tier, i) => (
                            <div key={i} className={`rounded-2xl border p-7 relative ${
                                tier.highlighted
                                    ? 'bg-white border-violet-300 shadow-xl shadow-violet-100/50 ring-1 ring-violet-200'
                                    : 'bg-white border-stone-200/60 hover:shadow-lg'
                            } transition-all`}>
                                {tier.badge && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-violet-600 text-white text-xs font-semibold shadow-lg">
                                            <Star className="w-3 h-3" /> {tier.badge}
                                        </span>
                                    </div>
                                )}
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-stone-900 mb-1">{tier.name}</h3>
                                    <p className="text-xs text-stone-500 mb-4">{tier.desc}</p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-4xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>{tier.price}</span>
                                        {tier.period && <span className="text-sm text-stone-400">{tier.period}</span>}
                                    </div>
                                </div>
                                <div className="space-y-3 mb-8">
                                    {tier.features.map((f, j) => (
                                        <div key={j} className="flex items-start gap-2.5">
                                            <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${tier.highlighted ? 'text-violet-500' : 'text-emerald-500'}`} />
                                            <span className="text-sm text-stone-600">{f}</span>
                                        </div>
                                    ))}
                                </div>
                                <Link
                                    to="/register"
                                    className={`block text-center text-sm font-semibold py-3 rounded-xl transition-all ${
                                        tier.highlighted
                                            ? 'bg-stone-900 text-white hover:bg-stone-800 shadow-lg shadow-stone-900/10'
                                            : 'border border-stone-300 text-stone-700 hover:bg-stone-50'
                                    }`}
                                >
                                    {tier.cta}
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20" style={{ backgroundColor: 'rgba(0,0,0,0.015)' }}>
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-stone-900 mb-3" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            Frequently Asked Questions
                        </h2>
                    </div>
                    <div className="space-y-3">
                        {faqs.map((faq, i) => (
                            <div key={i} className="bg-white rounded-xl border border-stone-200/60 overflow-hidden">
                                <button
                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full flex items-center justify-between p-5 text-left hover:bg-stone-50/50 transition-colors"
                                >
                                    <span className="text-sm font-semibold text-stone-900 pr-4">{faq.q}</span>
                                    <ChevronDown className={`w-4 h-4 text-stone-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                                </button>
                                {openFaq === i && (
                                    <div className="px-5 pb-5 -mt-1">
                                        <p className="text-sm text-stone-500 leading-relaxed">{faq.a}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 md:py-28">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-100/80 text-violet-700 text-xs font-semibold mb-6 border border-violet-200/50">
                        <Sparkles className="w-3.5 h-3.5" />
                        Ready to transform your sales?
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-5" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Stop Sending Generic Emails.<br />
                        <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">Start Nurturing Relationships.</span>
                    </h2>
                    <p className="text-stone-500 text-lg mb-8 max-w-xl mx-auto">
                        Join sales teams using AI to write better emails, build converting funnels, and close more deals.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/register" className="inline-flex items-center gap-2 text-base font-semibold bg-stone-900 text-white px-8 py-3.5 rounded-xl hover:bg-stone-800 transition-all shadow-lg shadow-stone-900/10">
                            Get Started Free <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <p className="text-xs text-stone-400 mt-4">No credit card required. Free plan available forever.</p>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-stone-200/60 py-10">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-7 h-7 rounded-lg bg-stone-900 flex items-center justify-center">
                                    <Sparkles className="w-3.5 h-3.5 text-white" />
                                </div>
                                <span className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>NurtureCRM</span>
                            </div>
                            <p className="text-xs text-stone-400 leading-relaxed mb-3">
                                AI-powered B2B sales CRM built for modern teams. A product of Multiplai.tech.
                            </p>
                            <span className="text-xs text-stone-300">by <a href="https://multiplai.tech" className="hover:text-stone-500 transition-colors">Multiplai.tech</a></span>
                        </div>
                        <div>
                            <h4 className="text-xs font-semibold text-stone-900 uppercase tracking-wider mb-3">Product</h4>
                            <div className="space-y-2">
                                <SmoothScrollLink href="#features" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">Features</SmoothScrollLink>
                                <SmoothScrollLink href="#ai-engine" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">AI Engine</SmoothScrollLink>
                                <SmoothScrollLink href="#website-builder" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">Website Builder</SmoothScrollLink>
                                <SmoothScrollLink href="#pricing" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">Pricing</SmoothScrollLink>
                            </div>
                        </div>
                        <div>
                            <h4 className="text-xs font-semibold text-stone-900 uppercase tracking-wider mb-3">Legal</h4>
                            <div className="space-y-2">
                                <a href="/privacy" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">Privacy Policy</a>
                                <a href="/terms" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">Terms & Conditions</a>
                                <a href="/ai-policy" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">AI Policy</a>
                                <a href="/data-policy" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">Data Policy</a>
                            </div>
                        </div>
                        <div>
                            <h4 className="text-xs font-semibold text-stone-900 uppercase tracking-wider mb-3">Company</h4>
                            <div className="space-y-2">
                                <a href="mailto:support@aicoaches.com" className="block text-xs text-stone-500 hover:text-stone-700 transition-colors">support@aicoaches.com</a>
                                <span className="block text-xs text-stone-400">AiCoaches dot com LLC</span>
                                <span className="block text-xs text-stone-400">Miami, FL</span>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-stone-200/60 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <p className="text-xs text-stone-400">&copy; {new Date().getFullYear()} AiCoaches dot com LLC. All rights reserved.</p>
                        <Link to="/login" className="text-xs text-stone-400 hover:text-stone-600 transition-colors">App Login</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
